import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import axios from 'axios';
import API_BASE from '../../utils/api';

const API = API_BASE;

const CATEGORY_SUGGESTIONS = ['Utilities', 'Maintenance', 'Supplies', 'Salaries - Other', 'Transport', 'Academic', 'Events', 'Misc'];
const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'online', label: 'Online' },
  { value: 'upi', label: 'UPI' },
  { value: 'net_banking', label: 'Net Banking' },
];

const emptyForm = {
  category: '',
  description: '',
  amount: '',
  date: new Date().toISOString().split('T')[0],
  vendor: '',
  paymentMode: 'cash',
  notes: '',
};

const Expenses = () => {
  const [activeTab, setActiveTab] = useState('record');
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [form, setForm] = useState(emptyForm);
  const [receiptFile, setReceiptFile] = useState(null);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', category: '', search: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;
      const [expRes, summaryRes] = await Promise.all([
        axios.get(`${API}/expenses`, { headers, params }),
        axios.get(`${API}/expenses/stats/summary`, { headers, params: { startDate: filters.startDate, endDate: filters.endDate } }),
      ]);
      setExpenses(expRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (activeTab === 'records') fetchExpenses();
  }, [activeTab, fetchExpenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category.trim() || !form.description.trim() || !form.amount || !form.date) {
      showMessage('error', 'Category, description, amount, and date are required');
      return;
    }
    try {
      setSaving(true);
      const res = await axios.post(`${API}/expenses`, {
        ...form,
        amount: parseFloat(form.amount),
      }, { headers });

      if (receiptFile) {
        const fd = new FormData();
        fd.append('receipt', receiptFile);
        await axios.post(`${API}/expenses/${res.data._id}/receipt`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
      }

      showMessage('success', 'Expense recorded successfully!');
      setForm(emptyForm);
      setReceiptFile(null);
      e.target.reset?.();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to record expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense record?')) return;
    try {
      await axios.delete(`${API}/expenses/${id}`, { headers });
      showMessage('success', 'Expense deleted');
      fetchExpenses();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to delete');
    }
  };

  const viewReceipt = (expenseId) => {
    window.open(`${API}/expenses/${expenseId}/receipt?token=${token}`, '_blank');
  };

  const categories = [...new Set([...CATEGORY_SUGGESTIONS, ...(summary?.byCategory?.map((c) => c.category) || [])])];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">🧾 Expenses</h1>
              <p className="text-gray-500 mt-1">Record and track school expenses</p>
            </div>
            <ProfileDropdown />
          </div>

          {message.text && (
            <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
              {message.text}
            </div>
          )}

          <div className="flex space-x-1 bg-gray-200 rounded-lg p-1 mb-6 w-fit">
            <button onClick={() => setActiveTab('record')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'record' ? 'bg-white text-indigo-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}>
              Record Expense
            </button>
            <button onClick={() => setActiveTab('records')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'records' ? 'bg-white text-indigo-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}>
              Expense Records
            </button>
          </div>

          {activeTab === 'record' && (
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-3xl">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">New Expense</h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <input list="expense-categories" value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g. Maintenance" required />
                    <datalist id="expense-categories">
                      {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
                    <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0" min="0" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                    <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                    <input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Optional" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                    <select value={form.paymentMode} onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                      {PAYMENT_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Receipt (PDF/JPG/PNG)</label>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="What was this expense for?" required />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows="2" placeholder="Any additional notes..." />
                </div>
                <button type="submit" disabled={saving}
                  className="bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 font-medium">
                  {saving ? 'Saving...' : 'Record Expense'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'records' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex flex-wrap gap-4 items-end mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2">
                    <option value="">All Categories</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                  <input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2" placeholder="Description or vendor" />
                </div>
                <button onClick={fetchExpenses} className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors">
                  🔍 Search
                </button>
              </div>

              {summary && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-purple-700">₹{summary.grandTotal.toLocaleString('en-IN')}</p>
                    <p className="text-sm text-purple-600">Total ({summary.totalCount} records)</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-700">₹{summary.thisMonthTotal.toLocaleString('en-IN')}</p>
                    <p className="text-sm text-blue-600">This Month ({summary.thisMonthCount})</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-700 font-medium mb-1">Top Category</p>
                    <p className="text-lg font-bold text-amber-800">
                      {summary.byCategory[0] ? `${summary.byCategory[0].category} — ₹${summary.byCategory[0].total.toLocaleString('en-IN')}` : '—'}
                    </p>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="text-center py-10 text-gray-500">Loading...</div>
              ) : expenses.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <p className="text-4xl mb-2">📭</p>
                  <p>No expense records found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="p-3 text-sm font-semibold text-gray-600">Date</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Category</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Description</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Vendor</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Mode</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Amount</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Recorded By</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Receipt</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((exp) => (
                        <tr key={exp._id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-sm">{new Date(exp.date).toLocaleDateString('en-IN')}</td>
                          <td className="p-3 text-sm">
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{exp.category}</span>
                          </td>
                          <td className="p-3 text-sm">{exp.description}</td>
                          <td className="p-3 text-sm text-gray-500">{exp.vendor || '—'}</td>
                          <td className="p-3 text-sm capitalize">{exp.paymentMode?.replace('_', ' ')}</td>
                          <td className="p-3 font-bold text-gray-800">₹{exp.amount.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-sm text-gray-500">{exp.createdBy?.name || '—'}</td>
                          <td className="p-3 text-sm">
                            {exp.receiptFilePath ? (
                              <button onClick={() => viewReceipt(exp._id)} className="text-indigo-600 hover:underline text-xs">View</button>
                            ) : <span className="text-gray-300 text-xs">None</span>}
                          </td>
                          <td className="p-3">
                            <button onClick={() => handleDelete(exp._id)}
                              className="text-red-500 hover:text-red-700 text-xs px-2 py-1 border border-red-300 rounded hover:bg-red-50">
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Expenses;
